import React, { useEffect, useState } from "react"
import { Modal } from "@mui/material"
import { getDocument, setTemplate } from "../../features/documents/documents"
import { useDispatch, useSelector } from "react-redux"
import ViewPdf from "../ViewPdf/ViewPdf"
import Spinner from "../Spiner/Spiner"
import Button from "../Button/Button"
import { showSnackBar } from "../Snackbar/Snackbar"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { AUDIO_EXTENSIONS, DOCUMENTS_AUDIO_FLAG, isAudioResult } from "../../constants/callRecordings"
import { REQUEST_ERRORS_FILES } from "../../constants/requestErrorFlags"

const UNPLAYABLE = 'This recording cannot be played.'

const PdfViewModal = ({ iconType, keyName, viewFileFunc, userId, isLSA = false, billingAccountId, lsaTypeDoc = 'legalServiceAgreement' }) => {
  const audioEnabled = useSelector(selectFeatureFlag(DOCUMENTS_AUDIO_FLAG))
  const filesStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_FILES))
  const typeDoc = audioEnabled && AUDIO_EXTENSIONS.includes(iconType)
    ? 'mp3'
    : iconType === 'vnd.openxmlformats-officedocument.wordprocessingml.document' ? 'docx' : (iconType?.length > 0 ? iconType : 'show')
  const [audio, setAudio] = useState(null)
  const [audioRetried, setAudioRetried] = useState(false)
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [docsContent, setDocsContent] = useState('')
  const [pageNum, setPageNum] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [pageDetails, setPageDetails] = useState(null)
  const [loading, setLoading] = useState(false)
  const [heightModalContent, setHeightModalContent] = useState(580)

  const handleOpen = async () => {
    setAudio(null)
    setAudioRetried(false)
    setOpen(true)
    setLoading(true)
    const opened = isLSA ? await getDocLSA() : await getDoc(keyName)
    if (viewFileFunc && opened !== false) {
      await viewFileFunc()
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const getDoc = async (key) => {
    const response = await dispatch(getDocument(key))
    if ((audioEnabled || filesStrict) && response?.error) {
      showSnackBar("error", typeof response.payload === 'string' ? response.payload : 'Unable to open the document')
      setLoading(false)
      setOpen(false)
      return false
    }
    if (audioEnabled && isAudioResult(response?.payload?.data)) {
      setAudio(response.payload.data)
      setLoading(false)
      return
    }
    setDocsContent(response?.payload?.data)
    setTimeout(() => {
      setLoading(false)
    }, 500)
  }

  const refreshAudio = async () => {
    if (audioRetried) {
      showSnackBar("error", UNPLAYABLE)
      return
    }
    setAudioRetried(true)
    const response = await dispatch(getDocument(keyName))
    if (isAudioResult(response?.payload?.data)) {
      setAudio(response.payload.data)
    } else {
      showSnackBar("error", UNPLAYABLE)
    }
  }

  const getDocLSA = async () => {
    const response = await dispatch(setTemplate({
      typeDoc: lsaTypeDoc,
      data: {
        billingAccountId,
        userId
      },
      useEstimatedPayments: true,
    }))

    if (response?.error) {
      showSnackBar("error", typeof response.payload === 'string' ? response.payload : (response.payload?.message || 'Unable to generate the document'))
      setLoading(false)
      setOpen(false)
      return
    }

    setDocsContent(response?.payload?.data)
    setTimeout(() => {
      setLoading(false)
    }, 500)
  }

  const adjustHeight = () => {
    const maxHeight = 780
    const currentHeight = window.innerHeight
    if (currentHeight < 819) {
      const newHeight = Math.round((currentHeight / 795) * (maxHeight - 120))
      const finalHeight = Math.min(newHeight, maxHeight)
      setHeightModalContent(finalHeight)
    } else {
      setHeightModalContent(maxHeight)
    }
  }
  window.addEventListener('load', adjustHeight)
  window.addEventListener('resize', adjustHeight)

  useEffect(() => {
    adjustHeight()
  }, [])
  return (
    <div>
      {isLSA ? <Button type="outlined" onClick={() => handleOpen()}>Review LSA</Button> :
        <div className="cursor-pointer font-bold text-text-secondary underline" onClick={() => handleOpen()}>
          show {typeDoc}
        </div>}

      <Modal
        open={open}
        closeAfterTransition
        onClose={handleClose}
      >
        <div className="flex items-center justify-center h-screen">
          <div
            className={`bg-surface rounded-2xl fixed flex-col justify-between py-3 max-h-content max-w-[880px] ${audio ? '' : 'min-h-[580px]'} w-content min-w-[480px] `}>
            <div className="absolute top-[20px] right-[20px] z-[999]">
              <div className="inline-flex justify-center items-center rounded-full cursor-pointer"
                onClick={handleClose}>
                <svg width={24} height={25} viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13.4032 12.4942L17.6976 8.20953C18.0892 7.8179 18.0892 7.18292 17.6976 6.79129C17.306 6.39965 16.671 6.39965 16.2794 6.79129L11.995 11.086L7.71063 6.79129C7.31902 6.39965 6.68409 6.39965 6.29248 6.79129C5.90087 7.18292 5.90087 7.8179 6.29248 8.20953L10.5869 12.4942L6.29248 16.7789C6.10342 16.9665 5.99707 17.2218 5.99707 17.4881C5.99707 17.7544 6.10342 18.0096 6.29248 18.1972C6.48 18.3863 6.73527 18.4926 7.00155 18.4926C7.26784 18.4926 7.52311 18.3863 7.71063 18.1972L11.995 13.9025L16.2794 18.1972C16.4669 18.3863 16.7222 18.4926 16.9885 18.4926C17.2548 18.4926 17.51 18.3863 17.6976 18.1972C17.8866 18.0096 17.993 17.7544 17.993 17.4881C17.993 17.2218 17.8866 16.9665 17.6976 16.7789L13.4032 12.4942Z"
                    fill="var(--color-gray-600)" />
                </svg>
              </div>
            </div>
            <div className="overflow-auto" style={{ maxHeight: heightModalContent }}>
              {audio ? (
                <div className="px-6 pt-16 pb-6">
                  <audio controls src={audio.url} className="w-full" onError={refreshAudio} />
                </div>
              ) : !isLSA && (keyName?.split(".").pop() === "png" || keyName?.split(".").pop() === "jpg" || keyName?.split(".").pop() === "jpeg") ? (

                <div style={{ display: loading && 'none' }}><img src={docsContent} alt="Image" /></div>
              ) : (<div style={{ display: loading && 'none' }}>
                <ViewPdf
                  width={800}
                  height={heightModalContent}
                  sePageN={setPageNum}
                  totalPages={totalPages}
                  pdf={docsContent}
                  setups={true}
                  setTotalPages={setTotalPages}
                  setPageD={setPageDetails}
                />
              </div>

              )}
            </div>
            {loading && <Spinner />}
          </div>
        </div>
      </Modal>
    </div>
  )
}
export default PdfViewModal
